import React, { useState, useMemo, useContext, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Alert,
  useTheme,
  Chip,
  InputAdornment,
  CircularProgress,
  Paper,
  Grid,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  LocalShipping as SupplierIcon,
  Search as SearchIcon,
  AccountCircle,
  AccountBalanceWallet,
  Business,
  AttachMoney,
  AccountBalance,
} from '@mui/icons-material';
import { DataContext } from "../../context/DataContext";
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';

ModuleRegistry.registerModules([AllCommunityModule]);

const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// Validation Schema
const SupplierSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name is too long')
    .required('Name is required'),
  supplierShortcut: Yup.string()
    .max(5, 'Shortcut is too long (max 5 characters)')
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  contact: Yup.string()
    .required('Contact number is required')
    .matches(/^[0-9+\-\s()]+$/, 'Invalid contact number'),
  email: Yup.string()
    .email('Invalid email address')
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  address: Yup.string()
    .max(500, 'Address is too long')
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  gstNumber: Yup.string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  openingBalanceCash: Yup.number()
    .typeError('Opening balance must be a number')
    .min(0, 'Balance cannot be negative')
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  openingBalanceBank: Yup.number()
    .typeError('Opening balance must be a number')
    .min(0, 'Balance cannot be negative')
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  openingBalanceFine: Yup.number()
    .typeError('Fine balance must be a number')
    .min(0, 'Fine cannot be negative')
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  bankName: Yup.string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  accountHolder: Yup.string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  accountNumber: Yup.string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  ifscCode: Yup.string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  branch: Yup.string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
});

// Initial values
const initialValues = {
  name: '',
  supplierShortcut: '',
  contact: '',
  email: '',
  address: '',
  gstNumber: '',
  openingBalanceCash: '',
  openingBalanceBank: '',
  openingBalanceFine: '',
  balanceValueCash: '',
  balanceValueBank: '',
  balanceFine: '',
  bankName: '',
  accountHolder: '',
  accountNumber: '',
  ifscCode: '',
  branch: '',
};

// Supplier Form Component
const SupplierForm = ({
  formik,
  isDark,
  isSaving,
  editingSupplier,
  handleCloseDrawer
}) => (
  <Form>
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              background: isDark
                ? 'linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)'
                : 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
          >
            {editingSupplier ? '✏️ Edit Supplier' : '👥 Add Supplier'}
          </Typography>
          <IconButton
            onClick={handleCloseDrawer}
            sx={{
              borderRadius: 2,
              backdropFilter: 'blur(10px)',
              backgroundColor: alpha(
                isDark ? '#1e293b' : '#ffffff',
                isDark ? 0.4 : 0.7
              ),
              border: `1px solid ${alpha(
                isDark ? '#475569' : '#e2e8f0',
                isDark ? 0.2 : 0.4
              )}`,
              color: isDark ? '#e2e8f0' : '#475569',
              width: 36,
              height: 36,
              '&:hover': {
                backgroundColor: alpha(
                  isDark ? '#1e293b' : '#ffffff',
                  isDark ? 0.6 : 0.9
                ),
                transform: 'scale(1.05)',
              },
            }}
            disabled={isSaving}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: isDark ? '#94a3b8' : '#64748b',
            fontWeight: 400,
            fontSize: '0.875rem',
          }}
        >
          {editingSupplier ? 'Update supplier information' : 'Enter supplier details'}
        </Typography>
      </Box>

      {/* Form Content - Scrollable Area */}
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 1, mb: 3, pt: 2 }}>
        {/* Personal Details Card */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            backgroundColor: alpha(
              isDark ? '#1e293b' : '#ffffff',
              isDark ? 0.4 : 0.6
            ),
            border: `1px solid ${alpha(
              isDark ? '#334155' : '#e2e8f0',
              isDark ? 0.2 : 0.3
            )}`,
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: alpha(
                isDark ? '#475569' : '#cbd5e1',
                isDark ? 0.3 : 0.4
              ),
            }
          }}
        >
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%)'
                    : 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
                  border: `1px solid ${alpha(
                    isDark ? '#3b82f6' : '#10b981',
                    isDark ? 0.2 : 0.15
                  )}`,
                }}
              >
                <AccountCircle sx={{
                  color: isDark ? '#60a5fa' : '#10b981',
                  fontSize: 18
                }} />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: isDark ? '#e2e8f0' : '#1e293b',
                  fontSize: '0.95rem',
                }}
              >
                Personal Details
              </Typography>
            </Box>

            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.75,
                    fontWeight: 500,
                    color: isDark ? '#cbd5e1' : '#475569',
                    fontSize: '0.8rem',
                  }}
                >
                  Name <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Field name="name">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Enter supplier name"
                      disabled={isSaving}
                      size="small"
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          backgroundColor: alpha(
                            isDark ? '#0f172a' : '#ffffff',
                            isDark ? 0.6 : 0.8
                          ),
                          border: `1px solid ${alpha(
                            isDark ? '#475569' : '#e2e8f0',
                            isDark ? 0.3 : 0.4
                          )}`,
                          '&:hover': {
                            borderColor: alpha(
                              isDark ? '#60a5fa' : '#3b82f6',
                              isDark ? 0.5 : 0.6
                            ),
                            backgroundColor: alpha(
                              isDark ? '#0f172a' : '#ffffff',
                              isDark ? 0.7 : 0.9
                            ),
                          },
                          '&.Mui-focused': {
                            borderColor: isDark ? '#60a5fa' : '#3b82f6',
                            borderWidth: '1.5px',
                            backgroundColor: alpha(
                              isDark ? '#0f172a' : '#ffffff',
                              isDark ? 0.7 : 0.9
                            ),
                          }
                        },
                        '& .MuiOutlinedInput-input': {
                          color: isDark ? '#e2e8f0' : '#1e293b',
                          fontSize: '0.875rem',
                          padding: '10px 12px',
                        },
                      }}
                    />
                  )}
                </Field>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.75,
                    fontWeight: 500,
                    color: isDark ? '#cbd5e1' : '#475569',
                    fontSize: '0.8rem',
                  }}
                >
                  Supplier Shortcut
                </Typography>
                <Field name="supplierShortcut">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Enter shortcut (e.g., REL, TIT, JOH)"
                      disabled={isSaving}
                      size="small"
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          backgroundColor: alpha(
                            isDark ? '#0f172a' : '#ffffff',
                            isDark ? 0.6 : 0.8
                          ),
                          border: `1px solid ${alpha(
                            isDark ? '#475569' : '#e2e8f0',
                            isDark ? 0.3 : 0.4
                          )}`,
                          '&:hover': {
                            borderColor: alpha(
                              isDark ? '#60a5fa' : '#3b82f6',
                              isDark ? 0.5 : 0.6
                            ),
                          },
                          '&.Mui-focused': {
                            borderColor: isDark ? '#60a5fa' : '#3b82f6',
                            borderWidth: '1.5px',
                            backgroundColor: alpha(
                              isDark ? '#0f172a' : '#ffffff',
                              isDark ? 0.7 : 0.9
                            ),
                          }
                        },
                        '& .MuiOutlinedInput-input': {
                          color: isDark ? '#e2e8f0' : '#1e293b',
                          fontSize: '0.875rem',
                          padding: '10px 12px',
                        },
                      }}
                    />
                  )}
                </Field>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.75,
                    fontWeight: 500,
                    color: isDark ? '#cbd5e1' : '#475569',
                    fontSize: '0.8rem',
                  }}
                >
                  Contact <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Field name="contact">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Enter phone number"
                      disabled={isSaving}
                      size="small"
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          backgroundColor: alpha(
                            isDark ? '#0f172a' : '#ffffff',
                            isDark ? 0.6 : 0.8
                          ),
                          border: `1px solid ${alpha(
                            isDark ? '#475569' : '#e2e8f0',
                            isDark ? 0.3 : 0.4
                          )}`,
                          '&:hover': {
                            borderColor: alpha(
                              isDark ? '#60a5fa' : '#3b82f6',
                              isDark ? 0.5 : 0.6
                            ),
                          }
                        },
                        '& .MuiOutlinedInput-input': {
                          color: isDark ? '#e2e8f0' : '#1e293b',
                          fontSize: '0.875rem',
                          padding: '10px 12px',
                        },
                      }}
                    />
                  )}
                </Field>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.75,
                    fontWeight: 500,
                    color: isDark ? '#cbd5e1' : '#475569',
                    fontSize: '0.8rem',
                  }}
                >
                  Email Address
                </Typography>
                <Field name="email">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="email"
                      placeholder="Enter email address"
                      disabled={isSaving}
                      size="small"
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          backgroundColor: alpha(
                            isDark ? '#0f172a' : '#ffffff',
                            isDark ? 0.6 : 0.8
                          ),
                          border: `1px solid ${alpha(
                            isDark ? '#475569' : '#e2e8f0',
                            isDark ? 0.3 : 0.4
                          )}`,
                          '&:hover': {
                            borderColor: alpha(
                              isDark ? '#60a5fa' : '#3b82f6',
                              isDark ? 0.5 : 0.6
                            ),
                          }
                        },
                        '& .MuiOutlinedInput-input': {
                          color: isDark ? '#e2e8f0' : '#1e293b',
                          fontSize: '0.875rem',
                          padding: '10px 12px',
                        },
                      }}
                    />
                  )}
                </Field>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Business Details Card */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            backgroundColor: alpha(
              isDark ? '#1e293b' : '#ffffff',
              isDark ? 0.4 : 0.6
            ),
            border: `1px solid ${alpha(
              isDark ? '#334155' : '#e2e8f0',
              isDark ? 0.2 : 0.3
            )}`,
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: alpha(
                isDark ? '#475569' : '#cbd5e1',
                isDark ? 0.3 : 0.4
              ),
            }
          }}
        >
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%)'
                    : 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
                  border: `1px solid ${alpha(
                    isDark ? '#3b82f6' : '#10b981',
                    isDark ? 0.2 : 0.15
                  )}`,
                }}
              >
                <Business sx={{
                  color: isDark ? '#60a5fa' : '#10b981',
                  fontSize: 18
                }} />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: isDark ? '#e2e8f0' : '#1e293b',
                  fontSize: '0.95rem',
                }}
              >
                Business Details
              </Typography>
            </Box>

            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.75,
                    fontWeight: 500,
                    color: isDark ? '#cbd5e1' : '#475569',
                    fontSize: '0.8rem',
                  }}
                >
                  GST Number
                </Typography>
                <Field name="gstNumber">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Enter GST number"
                      disabled={isSaving}
                      size="small"
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          backgroundColor: alpha(
                            isDark ? '#0f172a' : '#ffffff',
                            isDark ? 0.6 : 0.8
                          ),
                          border: `1px solid ${alpha(
                            isDark ? '#475569' : '#e2e8f0',
                            isDark ? 0.3 : 0.4
                          )}`,
                          '&:hover': {
                            borderColor: alpha(
                              isDark ? '#60a5fa' : '#3b82f6',
                              isDark ? 0.5 : 0.6
                            ),
                          }
                        },
                        '& .MuiOutlinedInput-input': {
                          color: isDark ? '#e2e8f0' : '#1e293b',
                          fontSize: '0.875rem',
                          padding: '10px 12px',
                        },
                      }}
                    />
                  )}
                </Field>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.75,
                    fontWeight: 500,
                    color: isDark ? '#cbd5e1' : '#475569',
                    fontSize: '0.8rem',
                  }}
                >
                  Business Address
                </Typography>
                <Field name="address">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Enter full address"
                      multiline
                      rows={3}
                      disabled={isSaving}
                      size="small"
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          backgroundColor: alpha(
                            isDark ? '#0f172a' : '#ffffff',
                            isDark ? 0.6 : 0.8
                          ),
                          border: `1px solid ${alpha(
                            isDark ? '#475569' : '#e2e8f0',
                            isDark ? 0.3 : 0.4
                          )}`,
                          '&:hover': {
                            borderColor: alpha(
                              isDark ? '#60a5fa' : '#3b82f6',
                              isDark ? 0.5 : 0.6
                            ),
                          }
                        },
                        '& .MuiOutlinedInput-input': {
                          color: isDark ? '#e2e8f0' : '#1e293b',
                          fontSize: '0.875rem',
                          padding: '10px 12px',
                        },
                      }}
                    />
                  )}
                </Field>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Opening Balances Card */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            backgroundColor: alpha(
              isDark ? '#1e293b' : '#ffffff',
              isDark ? 0.4 : 0.6
            ),
            border: `1px solid ${alpha(
              isDark ? '#334155' : '#e2e8f0',
              isDark ? 0.2 : 0.3
            )}`,
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: alpha(
                isDark ? '#475569' : '#cbd5e1',
                isDark ? 0.3 : 0.4
              ),
            }
          }}
        >
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.2) 100%)'
                    : 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%)',
                  border: `1px solid ${alpha(
                    isDark ? '#f59e0b' : '#f59e0b',
                    isDark ? 0.2 : 0.15
                  )}`,
                }}
              >
                <AccountBalanceWallet sx={{
                  color: isDark ? '#fbbf24' : '#f59e0b',
                  fontSize: 18
                }} />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: isDark ? '#e2e8f0' : '#1e293b',
                  fontSize: '0.95rem',
                }}
              >
                Opening Balances
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? '#cbd5e1' : '#475569',
                      fontSize: '0.8rem',
                    }}
                  >
                    Opening Balance (Cash)
                  </Typography>
                  <Field name="openingBalanceCash">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="0.00"
                        disabled={isSaving || editingSupplier}
                        size="small"
                        type="text"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoney sx={{
                                fontSize: 16,
                                color: isDark ? '#fbbf24' : '#f59e0b'
                              }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? '#0f172a' : '#ffffff',
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? '#475569' : '#e2e8f0',
                              isDark ? 0.3 : 0.4
                            )}`,
                            '&:hover': {
                              borderColor: alpha(
                                isDark ? '#fbbf24' : '#f59e0b',
                                isDark ? 0.5 : 0.6
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontSize: '0.875rem',
                            padding: '10px 12px',
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? '#cbd5e1' : '#475569',
                      fontSize: '0.8rem',
                    }}
                  >
                    Opening Balance (Bank)
                  </Typography>
                  <Field name="openingBalanceBank">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="0.00"
                        disabled={isSaving || editingSupplier}
                        size="small"
                        type="text"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccountBalance sx={{
                                fontSize: 16,
                                color: isDark ? '#fbbf24' : '#f59e0b'
                              }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? '#0f172a' : '#ffffff',
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? '#475569' : '#e2e8f0',
                              isDark ? 0.3 : 0.4
                            )}`,
                            '&:hover': {
                              borderColor: alpha(
                                isDark ? '#fbbf24' : '#f59e0b',
                                isDark ? 0.5 : 0.6
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontSize: '0.875rem',
                            padding: '10px 12px',
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? '#cbd5e1' : '#475569',
                      fontSize: '0.8rem',
                    }}
                  >
                    Opening Balance (Fine in gms)
                  </Typography>
                  <Field name="openingBalanceFine">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="0.00"
                        disabled={isSaving || editingSupplier}
                        size="small"
                        type="text"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccountBalance sx={{
                                fontSize: 16,
                                color: isDark ? '#fbbf24' : '#f59e0b'
                              }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? '#0f172a' : '#ffffff',
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? '#475569' : '#e2e8f0',
                              isDark ? 0.3 : 0.4
                            )}`,
                            '&:hover': {
                              borderColor: alpha(
                                isDark ? '#fbbf24' : '#f59e0b',
                                isDark ? 0.5 : 0.6
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontSize: '0.875rem',
                            padding: '10px 12px',
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>
              </Grid>
            </Grid>

            <Typography
              variant="caption"
              sx={{
                mt: 1.5,
                display: 'block',
                color: isDark ? '#94A3B8' : '#64748B',
                fontSize: '0.75rem',
                fontStyle: 'italic',
              }}
            >
              {editingSupplier ? "Opening Balance can only be set while creating a new supplier, you can't change it." : "Optional: Initial balances for supplier account. Leave as 0 if none, CANNOT be updated later."}
            </Typography>
          </CardContent>
        </Card>

        {/* Bank Details Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            backgroundColor: alpha(
              isDark ? '#1e293b' : '#ffffff',
              isDark ? 0.4 : 0.6
            ),
            border: `1px solid ${alpha(
              isDark ? '#334155' : '#e2e8f0',
              isDark ? 0.2 : 0.3
            )}`,
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: alpha(
                isDark ? '#475569' : '#cbd5e1',
                isDark ? 0.3 : 0.4
              ),
            }
          }}
        >
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)'
                    : 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)',
                  border: `1px solid ${alpha(
                    isDark ? '#8b5cf6' : '#8b5cf6',
                    isDark ? 0.2 : 0.15
                  )}`,
                }}
              >
                <AccountBalanceWallet sx={{
                  color: isDark ? '#a78bfa' : '#8b5cf6',
                  fontSize: 18
                }} />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: isDark ? '#e2e8f0' : '#1e293b',
                  fontSize: '0.95rem',
                }}
              >
                Bank Details
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? '#cbd5e1' : '#475569',
                      fontSize: '0.8rem',
                    }}
                  >
                    Bank Name
                  </Typography>
                  <Field name="bankName">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter bank name"
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? '#0f172a' : '#ffffff',
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? '#475569' : '#e2e8f0',
                              isDark ? 0.3 : 0.4
                            )}`,
                            '&:hover': {
                              borderColor: alpha(
                                isDark ? '#60a5fa' : '#3b82f6',
                                isDark ? 0.5 : 0.6
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontSize: '0.875rem',
                            padding: '10px 12px',
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? '#cbd5e1' : '#475569',
                      fontSize: '0.8rem',
                    }}
                  >
                    Account Holder Name
                  </Typography>
                  <Field name="accountHolder">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter account holder name"
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? '#0f172a' : '#ffffff',
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? '#475569' : '#e2e8f0',
                              isDark ? 0.3 : 0.4
                            )}`,
                            '&:hover': {
                              borderColor: alpha(
                                isDark ? '#60a5fa' : '#3b82f6',
                                isDark ? 0.5 : 0.6
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontSize: '0.875rem',
                            padding: '10px 12px',
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? '#cbd5e1' : '#475569',
                      fontSize: '0.8rem',
                    }}
                  >
                    Account Number
                  </Typography>
                  <Field name="accountNumber">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter account number"
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? '#0f172a' : '#ffffff',
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? '#475569' : '#e2e8f0',
                              isDark ? 0.3 : 0.4
                            )}`,
                            '&:hover': {
                              borderColor: alpha(
                                isDark ? '#60a5fa' : '#3b82f6',
                                isDark ? 0.5 : 0.6
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontSize: '0.875rem',
                            padding: '10px 12px',
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? '#cbd5e1' : '#475569',
                      fontSize: '0.8rem',
                    }}
                  >
                    IFSC Code
                  </Typography>
                  <Field name="ifscCode">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter IFSC code"
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? '#0f172a' : '#ffffff',
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? '#475569' : '#e2e8f0',
                              isDark ? 0.3 : 0.4
                            )}`,
                            '&:hover': {
                              borderColor: alpha(
                                isDark ? '#60a5fa' : '#3b82f6',
                                isDark ? 0.5 : 0.6
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontSize: '0.875rem',
                            padding: '10px 12px',
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.75,
                      fontWeight: 500,
                      color: isDark ? '#cbd5e1' : '#475569',
                      fontSize: '0.8rem',
                    }}
                  >
                    Branch Name
                  </Typography>
                  <Field name="branch">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter branch name"
                        disabled={isSaving}
                        size="small"
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? '#0f172a' : '#ffffff',
                              isDark ? 0.6 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? '#475569' : '#e2e8f0',
                              isDark ? 0.3 : 0.4
                            )}`,
                            '&:hover': {
                              borderColor: alpha(
                                isDark ? '#60a5fa' : '#3b82f6',
                                isDark ? 0.5 : 0.6
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontSize: '0.875rem',
                            padding: '10px 12px',
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Footer Actions */}
      <Box sx={{ pt: 2, borderTop: `1px solid ${alpha(isDark ? '#334155' : '#e2e8f0', 0.2)}` }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleCloseDrawer}
            disabled={isSaving}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              backgroundColor: alpha(
                isDark ? '#1e293b' : '#ffffff',
                isDark ? 0.4 : 0.8
              ),
              border: `1.5px solid ${alpha(
                isDark ? '#475569' : '#cbd5e1',
                isDark ? 0.3 : 0.4
              )}`,
              color: isDark ? '#e2e8f0' : '#475569',
              py: 1.25,
              fontSize: '0.875rem',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: alpha(
                  isDark ? '#1e293b' : '#ffffff',
                  isDark ? 0.6 : 0.95
                ),
                borderColor: alpha(
                  isDark ? '#60a5fa' : '#3b82f6',
                  isDark ? 0.5 : 0.6
                ),
                transform: 'translateY(-1px)',
              },
              '&.Mui-disabled': {
                opacity: 0.5,
                borderColor: alpha(isDark ? '#475569' : '#cbd5e1', 0.2),
              }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isSaving || !formik.isValid || !formik.dirty}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              background: isDark
                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              boxShadow: isDark
                ? '0 4px 12px rgba(59, 130, 246, 0.25)'
                : '0 4px 12px rgba(37, 99, 235, 0.25)',
              color: '#fff',
              py: 1.25,
              fontSize: '0.875rem',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: isDark
                  ? 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                  : 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                boxShadow: isDark
                  ? '0 6px 16px rgba(59, 130, 246, 0.35)'
                  : '0 6px 16px rgba(37, 99, 235, 0.35)',
                transform: 'translateY(-1px)',
              },
              '&.Mui-disabled': {
                background: alpha(
                  isDark ? '#3b82f6' : '#2563eb',
                  0.3
                ),
                boxShadow: 'none',
                color: alpha('#fff', 0.5),
              }
            }}
          >
            {isSaving ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <CircularProgress
                  size={16}
                  sx={{ color: '#fff' }}
                />
                <span>Saving...</span>
              </Box>
            ) : (
              editingSupplier ? 'Update Supplier' : 'Save Supplier'
            )}
          </Button>
        </Stack>
      </Box>
    </Box>
  </Form>
);

// Bank Details Modal Component
const BankDetailsModal = ({ open, onClose, supplier, isDark }) => {
  const [copiedField, setCopiedField] = useState(null);

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (!supplier) return null;

  const bankDetails = {
    bankName: supplier.bankName,
    accountHolder: supplier.accountHolder,
    accountNumber: supplier.accountNumber,
    ifscCode: supplier.ifscCode,
    branch: supplier.branch,
    gstNumber: supplier.gstNumber,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: isDark
            ? 'rgba(15, 23, 42, 0.95)'
            : 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${isDark
            ? alpha('#1e293b', 0.5)
            : alpha('#e2e8f0', 0.8)
            }`,
          borderRadius: '20px',
          boxShadow: isDark
            ? `0 25px 50px -12px rgba(0, 0, 0, 0.5)`
            : `0 25px 50px -12px rgba(0, 0, 0, 0.15)`,
          width: '90%',
          maxWidth: '700px',
        }
      }}
    >
      <Box sx={{
        p: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: isDark
            ? 'linear-gradient(90deg, #3b82f6, #8b5cf6)'
            : 'linear-gradient(90deg, #10b981, #3b82f6)',
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDark
                ? alpha('#3b82f6', 0.15)
                : alpha('#10b981', 0.15),
              color: isDark ? '#3b82f6' : '#10b981',
              boxShadow: isDark
                ? '0 8px 16px rgba(59, 130, 246, 0.2)'
                : '0 8px 16px rgba(16, 185, 129, 0.2)',
            }}>
              <AccountBalanceWallet sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.25rem',
                letterSpacing: '-0.5px',
              }}>
                Bank Details
              </Typography>
              <Typography variant="body2" sx={{
                color: isDark ? '#94a3b8' : '#64748b',
                mt: 0.5,
              }}>
                {supplier.name}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: isDark ? '#94a3b8' : '#64748b',
              backgroundColor: isDark
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.05)',
              '&:hover': {
                backgroundColor: isDark
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.1)',
                color: isDark ? '#ffffff' : '#000000',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent>
        <Stack spacing={2}>
          {Object.entries(bankDetails).map(([key, value]) => (
            <Box key={key} sx={{
              p: 2,
              borderRadius: '12px',
              backgroundColor: isDark
                ? 'rgba(255, 255, 255, 0.03)'
                : 'rgba(0, 0, 0, 0.02)',
              border: `1px solid ${isDark
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(226, 232, 240, 0.5)'
                }`,
            }}>
              <Typography variant="caption" sx={{
                color: isDark ? '#94a3b8' : '#64748b',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.75rem',
                mb: 1,
                display: 'block',
              }}>
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '1rem',
                  flex: 1,
                }}>
                  {value || 'Not specified'}
                </Typography>
                {value && (
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(value, key)}
                    sx={{
                      width: 32,
                      height: 32,
                      color: copiedField === key ? '#10b981' : isDark ? '#94a3b8' : '#64748b',
                      backgroundColor: isDark
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    {copiedField === key ? '✓' : '📋'}
                  </IconButton>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderRadius: '12px',
            borderColor: isDark
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(226, 232, 240, 0.8)',
            color: isDark ? '#94a3b8' : '#64748b',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              borderColor: isDark ? '#3b82f6' : '#10b981',
              backgroundColor: isDark
                ? alpha('#3b82f6', 0.05)
                : alpha('#10b981', 0.05),
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Add this component after BankDetailsModal and before the SuppliersPage component
const LedgerBalanceModal = ({ open, onClose, supplier, isDark }) => {
  // Calculate totals
  const openingTotal = useMemo(() => {
    const cash = parseFloat(supplier?.openingBalanceCash || 0);
    const bank = parseFloat(supplier?.openingBalanceBank || 0);
    return cash + bank;
  }, [supplier]);

  const currentTotal = useMemo(() => {
    const cash = parseFloat(supplier?.balanceValueCash || 0);
    const bank = parseFloat(supplier?.balanceValueBank || 0);
    return cash + bank;
  }, [supplier]);

  const netChange = useMemo(() => {
    return currentTotal - openingTotal;
  }, [currentTotal, openingTotal]);

  if (!supplier) return null;

  const balanceData = [
    {
      type: 'Opening Balance',
      cash: supplier.openingBalanceCash || 0,
      bank: supplier.openingBalanceBank || 0,
      fine: supplier.openingBalanceFine || 0,
      total: openingTotal,
      isOpening: true
    },
    {
      type: 'Current Balance',
      cash: supplier.balanceValueCash || 0,
      bank: supplier.balanceValueBank || 0,
      fine: supplier.balanceFine || 0,
      total: currentTotal,
      isCurrent: true
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: isDark
            ? 'rgba(15, 23, 42, 0.95)'
            : 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${isDark
            ? alpha('#1e293b', 0.5)
            : alpha('#e2e8f0', 0.8)
            }`,
          borderRadius: '24px',
          boxShadow: isDark
            ? `0 25px 50px -12px rgba(0, 0, 0, 0.5)`
            : `0 25px 50px -12px rgba(0, 0, 0, 0.15)`,
          width: '90%',
          maxWidth: '900px',
          overflow: 'hidden',
        }
      }}
    >
      <Box sx={{
        p: 4,
        position: 'relative',
        overflow: 'hidden',
        background: isDark
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)'
          : 'linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(241, 245, 249, 0.95) 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: isDark
            ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
            : 'linear-gradient(90deg, #f59e0b, #fbbf24)',
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 64,
              height: 64,
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDark
                ? alpha('#f59e0b', 0.15)
                : alpha('#f59e0b', 0.15),
              color: isDark ? '#f59e0b' : '#f59e0b',
            }}>
              <AccountBalance sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.5rem',
                letterSpacing: '-0.5px',
                mb: 0.5,
              }}>
                Ledger Balance
              </Typography>
              <Typography variant="body2" sx={{
                color: isDark ? '#94a3b8' : '#64748b',
              }}>
                {supplier.name} • {supplier.gstNumber || 'No GST'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: isDark ? '#94a3b8' : '#64748b',
              backgroundColor: isDark
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.05)',
              '&:hover': {
                backgroundColor: isDark
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.1)',
                color: isDark ? '#ffffff' : '#000000',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Summary Cards */}
        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 10 }}>
          <Card sx={{
            p: 2,
            borderRadius: '16px',
            backgroundColor: isDark
              ? alpha('#f59e0b', 0.08)
              : alpha('#f59e0b', 0.08),
            border: `1px solid ${alpha('#f59e0b', 0.2)}`,
            boxShadow: 'none',
          }}>
            <Typography variant="caption" sx={{
              color: isDark ? '#fbbf24' : '#d97706',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.75rem',
              mb: 1,
              display: 'block',
            }}>
              Opening Balance
            </Typography>
            <Typography variant="h4" sx={{
              fontWeight: 800,
              color: isDark ? '#fbbf24' : '#d97706',
              fontSize: '1.75rem',
            }}>
              ₹{openingTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Card>

          <Card sx={{
            p: 2,
            borderRadius: '16px',
            backgroundColor: isDark
              ? alpha('#10b981', 0.08)
              : alpha('#10b981', 0.08),
            border: `1px solid ${alpha('#10b981', 0.2)}`,
            boxShadow: 'none',
          }}>
            <Typography variant="caption" sx={{
              color: isDark ? '#34d399' : '#059669',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.75rem',
              mb: 1,
              display: 'block',
            }}>
              Current Balance
            </Typography>
            <Typography variant="h4" sx={{
              fontWeight: 800,
              color: isDark ? '#34d399' : '#059669',
              fontSize: '1.75rem',
            }}>
              ₹{currentTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Card>

          <Card sx={{
            p: 2,
            borderRadius: '16px',
            backgroundColor: isDark
              ? netChange >= 0 ? alpha('#10b981', 0.08) : alpha('#ef4444', 0.08)
              : netChange >= 0 ? alpha('#10b981', 0.08) : alpha('#ef4444', 0.08),
            border: `1px solid ${netChange >= 0 ? alpha('#10b981', 0.2) : alpha('#ef4444', 0.2)}`,
            boxShadow: 'none',
          }}>
            <Typography variant="caption" sx={{
              color: netChange >= 0
                ? isDark ? '#34d399' : '#059669'
                : isDark ? '#f87171' : '#dc2626',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.75rem',
              mb: 1,
              display: 'block',
            }}>
              Net Change
            </Typography>
            <Typography variant="h4" sx={{
              fontWeight: 800,
              color: netChange >= 0
                ? isDark ? '#34d399' : '#059669'
                : isDark ? '#f87171' : '#dc2626',
              fontSize: '1.75rem',
            }}>
              {netChange >= 0 ? '+' : ''}₹{Math.abs(netChange).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Card>
        </Box>
      </Box>

      <DialogContent sx={{ p: 2 }}>
        {/* Balance Details Table */}
        <Box sx={{
          mb: 4,
          overflow: 'hidden',
          borderRadius: '16px',
          border: `1px solid ${isDark
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(226, 232, 240, 0.8)'
            }`,
        }}>
          <Box sx={{
            p: 3,
            backgroundColor: isDark
              ? 'rgba(30, 41, 59, 0.3)'
              : 'rgba(248, 250, 252, 0.8)',
            borderBottom: `1px solid ${isDark
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(226, 232, 240, 0.8)'
              }`,
          }}>
            <Typography variant="h6" sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1.1rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
              <AccountBalanceWallet sx={{ fontSize: 20, color: isDark ? '#f59e0b' : '#f59e0b' }} />
              Balance Breakdown
            </Typography>
          </Box>

          <Box sx={{ p: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{
                  backgroundColor: isDark
                    ? 'rgba(255, 255, 255, 0.03)'
                    : 'rgba(0, 0, 0, 0.02)',
                }}>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'left',
                    borderBottom: `1px solid ${isDark
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(226, 232, 240, 0.8)'
                      }`,
                    color: isDark ? '#94a3b8' : '#64748b',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    Type
                  </th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'right',
                    borderBottom: `1px solid ${isDark
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(226, 232, 240, 0.8)'
                      }`,
                    color: isDark ? '#94a3b8' : '#64748b',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    Cash (₹)
                  </th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'right',
                    borderBottom: `1px solid ${isDark
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(226, 232, 240, 0.8)'
                      }`,
                    color: isDark ? '#94a3b8' : '#64748b',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    Bank (₹)
                  </th>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'right',
                    borderBottom: `1px solid ${isDark
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(226, 232, 240, 0.8)'
                      }`,
                    color: isDark ? '#94a3b8' : '#64748b',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    Total (₹)
                  </th>
                </tr>
              </thead>
              <tbody>
                {balanceData.map((row, index) => (
                  <tr key={index} style={{
                    backgroundColor: index % 2 === 0
                      ? isDark
                        ? 'rgba(255, 255, 255, 0.02)'
                        : 'rgba(0, 0, 0, 0.01)'
                      : 'transparent',
                    borderBottom: `1px solid ${isDark
                      ? 'rgba(255, 255, 255, 0.03)'
                      : 'rgba(226, 232, 240, 0.5)'
                      }`,
                  }}>
                    <td style={{
                      padding: '20px',
                      color: 'text.primary',
                      fontWeight: row.isOpening ? 600 : row.isCurrent ? 600 : 500,
                      fontSize: '0.95rem',
                      borderRight: `1px solid ${isDark
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(226, 232, 240, 0.5)'
                        }`,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: row.isOpening
                            ? alpha('#f59e0b', 0.1)
                            : alpha('#10b981', 0.1),
                          color: row.isOpening ? '#f59e0b' : '#10b981',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                        }}>
                          {row.isOpening ? 'O' : 'C'}
                        </Box>
                        {row.type}
                      </Box>
                    </td>
                    <td style={{
                      padding: '20px',
                      textAlign: 'right',
                      color: row.isOpening
                        ? isDark ? '#fbbf24' : '#d97706'
                        : isDark ? '#34d399' : '#059669',
                      fontWeight: 700,
                      fontSize: '1rem',
                      borderRight: `1px solid ${isDark
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(226, 232, 240, 0.5)'
                        }`,
                    }}>
                      ₹{parseFloat(row.cash).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{
                      padding: '20px',
                      textAlign: 'right',
                      color: row.isOpening
                        ? isDark ? '#fbbf24' : '#d97706'
                        : isDark ? '#34d399' : '#059669',
                      fontWeight: 700,
                      fontSize: '1rem',
                      borderRight: `1px solid ${isDark
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(226, 232, 240, 0.5)'
                        }`,
                    }}>
                      ₹{parseFloat(row.bank).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{
                      padding: '20px',
                      textAlign: 'right',
                      color: row.isOpening
                        ? isDark ? '#fbbf24' : '#d97706'
                        : isDark ? '#34d399' : '#059669',
                      fontWeight: 800,
                      fontSize: '1.1rem',
                    }}>
                      ₹{parseFloat(row.total).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>

        {/* Fine Balance Card */}
        {parseFloat(supplier.balanceFine || 0) > 0 && (
          <Card sx={{
            p: 3,
            borderRadius: '16px',
            backgroundColor: isDark
              ? alpha('#8b5cf6', 0.08)
              : alpha('#8b5cf6', 0.08),
            border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
            boxShadow: 'none',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark
                  ? alpha('#8b5cf6', 0.15)
                  : alpha('#8b5cf6', 0.15),
                color: '#8b5cf6',
              }}>
                <AccountBalanceWallet sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '1rem',
                }}>
                  Fine Balance Details
                </Typography>
                <Typography variant="body2" sx={{
                  color: isDark ? '#a78bfa' : '#7c3aed',
                  fontSize: '0.875rem',
                }}>
                  Gold/Silver fine balance in grams
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" sx={{
                  color: isDark ? '#94a3b8' : '#64748b',
                  fontSize: '0.875rem',
                  mb: 0.5,
                }}>
                  Opening Fine
                </Typography>
                <Typography variant="h5" sx={{
                  fontWeight: 800,
                  color: '#8b5cf6',
                }}>
                  {parseFloat(supplier.openingBalanceFine || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gms
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{
                  color: isDark ? '#94a3b8' : '#64748b',
                  fontSize: '0.875rem',
                  mb: 0.5,
                }}>
                  Current Fine
                </Typography>
                <Typography variant="h5" sx={{
                  fontWeight: 800,
                  color: '#8b5cf6',
                }}>
                  {parseFloat(supplier.balanceFine || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gms
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{
                  color: isDark ? '#94a3b8' : '#64748b',
                  fontSize: '0.875rem',
                  mb: 0.5,
                }}>
                  Fine Change
                </Typography>
                <Typography variant="h5" sx={{
                  fontWeight: 800,
                  color: parseFloat(supplier.balanceFine || 0) >= parseFloat(supplier.openingBalanceFine || 0)
                    ? '#10b981'
                    : '#ef4444',
                }}>
                  {parseFloat(supplier.balanceFine || 0) >= parseFloat(supplier.openingBalanceFine || 0) ? '+' : ''}
                  {(parseFloat(supplier.balanceFine || 0) - parseFloat(supplier.openingBalanceFine || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gms
                </Typography>
              </Box>
            </Box>
          </Card>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderRadius: '12px',
            borderColor: isDark
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(226, 232, 240, 0.8)',
            color: isDark ? '#94a3b8' : '#64748b',
            fontWeight: 600,
            textTransform: 'none',
            px: 3,
            py: 1.5,
            '&:hover': {
              borderColor: isDark ? '#3b82f6' : '#10b981',
              backgroundColor: isDark
                ? alpha('#3b82f6', 0.05)
                : alpha('#10b981', 0.05),
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SuppliersPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const gridRef = useRef();

  const {
    suppliers,
    loading: contextLoading,
    addSupplier,
    updateSupplier,
    deleteSupplier,
    refresh
  } = useContext(DataContext);

  // State
  const [searchInput, setSearchInput] = useState('');
  const searchTerm = useDebounce(searchInput, 300);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSupplierId, setDeleteSupplierId] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [bankDetailsOpen, setBankDetailsOpen] = useState(false);
  const [ledgerBalanceOpen, setLedgerBalanceOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  // const [isGridReady, setIsGridReady] = useState(false);

  const handleEditSupplier = useCallback((supplier) => {
    setEditingSupplier(supplier);
    setDrawerOpen(true);
  }, []);

  const handleAddSupplier = useCallback(() => {
    setEditingSupplier(null);
    setDrawerOpen(true);
  }, []);

  const handleDeleteClick = useCallback((id) => {
    setDeleteSupplierId(id);
    setDeleteConfirmOpen(true);
  }, []);

  // AG Grid column definitions
  const columnDefs = useMemo(() => [
    {
      headerName: 'Sr. No.',
      valueGetter: 'node.rowIndex + 1',
      width: 75,
      maxWidth: 75,
      minWidth: 75,
      sortable: false,
      filter: false,
      // pinned: 'left',
      cellStyle: {
        textAlign: 'center',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      suppressSizeToFit: true,
    },
    {
      field: 'name',
      headerName: 'Supplier Name',
      minWidth: 250,
      flex: 2,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          height: '100%',
          py: 1
        }}>
          <Box sx={{
            width: 40,
            height: 40,
            minWidth: 40,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isDark
              ? alpha('#3b82f6', 0.1)
              : alpha('#10b981', 0.1),
            color: isDark ? '#3b82f6' : '#10b981',
            fontWeight: 700,
            fontSize: '1.1rem',
            flexShrink: 0
          }}>
            {params.value?.charAt(0)?.toUpperCase() || 'S'}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{
              fontWeight: 600,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {params.value}
            </Typography>
            {params.data.email && (
              <Typography variant="caption" sx={{
                color: 'text.secondary',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {params.data.email}
              </Typography>
            )}
          </Box>
        </Box>
      ),
      autoHeight: true,
    },
    {
      field: 'supplierShortcut',
      headerName: 'Shortcut',
      minWidth: 100,
      width: 120,
      flex: 0.5,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          py: 1
        }}>
          <Chip
            label={params.value || '—'}
            size="small"
            sx={{
              backgroundColor: isDark
                ? alpha('#8b5cf6', 0.1)
                : alpha('#8b5cf6', 0.1),
              border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
              color: '#8b5cf6',
              fontWeight: 600,
              fontSize: '0.85rem',
              fontFamily: 'monospace',
              letterSpacing: '0.5px',
            }}
          />
        </Box>
      ),
    },
    {
      field: 'contact',
      headerName: 'Contact',
      minWidth: 140,
      flex: 1,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => (
        <Box sx={{ py: 1 }}>
          <Typography sx={{
            color: isDark ? '#3b82f6' : '#10b981',
            fontWeight: 700,
            fontSize: '0.9rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {params.value}
          </Typography>
          <Typography variant="caption" sx={{
            color: 'text.secondary',
            display: 'block'
          }}>
            Mobile
          </Typography>
        </Box>
      ),
    },
    {
      field: 'gstNumber',
      headerName: 'GST Number',
      minWidth: 150,
      flex: 1,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          py: 1
        }}>
          <Chip
            label={params.value || 'No GST'}
            size="small"
            sx={{
              backgroundColor: isDark
                ? alpha('#8b5cf6', 0.1)
                : alpha('#8b5cf6', 0.1),
              border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
              color: '#8b5cf6',
              fontWeight: 500,
              fontSize: '0.85rem',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          />
        </Box>
      ),
    },
    {
      field: 'address',
      headerName: 'Address',
      minWidth: 200,
      flex: 2,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => (
        <Box sx={{
          py: 1,
          height: '100%',
          display: 'flex',
          alignItems: 'flex-start' // Changed from 'center' to 'flex-start' for better text wrapping
        }}>
          <Typography variant="body2" sx={{
            color: 'text.secondary',
            fontSize: '0.9rem',
            lineHeight: 1.5,
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            overflow: 'visible'
          }}>
            {params.value || 'No address'}
          </Typography>
        </Box>
      ),
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        display: 'flex',
        alignItems: 'flex-start',
        whiteSpace: 'normal',
        overflow: 'visible',
        lineHeight: 'normal'
      },
    },
    {
      headerName: 'Bank Details',
      minWidth: 130,
      flex: 0.8,
      filter: false,
      sortable: false,
      cellRenderer: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          py: 1
        }}>
          {params.data.bankName ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSelectedSupplier(params.data);
                setBankDetailsOpen(true);
              }}
              sx={{
                borderRadius: '8px',
                borderColor: isDark
                  ? alpha('#3b82f6', 0.3)
                  : alpha('#10b981', 0.3),
                backgroundColor: isDark
                  ? alpha('#3b82f6', 0.1)
                  : alpha('#10b981', 0.1),
                color: isDark ? '#3b82f6' : '#10b981',
                fontSize: '0.85rem',
                fontWeight: 600,
                textTransform: 'none',
                px: 1.5,
                py: 0.8,
                minWidth: 'auto',
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              Bank
            </Button>
          ) : (
            <Chip
              label="No Bank"
              size="small"
              sx={{
                backgroundColor: isDark
                  ? alpha('#ef4444', 0.1)
                  : alpha('#ef4444', 0.1),
                border: `1px solid ${alpha('#ef4444', 0.2)}`,
                color: '#ef4444',
                fontSize: '0.85rem',
                fontWeight: 500,
                width: '100%',
                justifyContent: 'center'
              }}
            />
          )}
        </Box>
      ),
      suppressSizeToFit: true,
    },
    {
      headerName: 'Balance Details',
      minWidth: 150,
      flex: 1,
      filter: false,
      sortable: false,
      cellRenderer: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          py: 1
        }}>
          {params.data.name ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSelectedSupplier(params.data);
                setLedgerBalanceOpen(true);
              }}
              sx={{
                borderRadius: '8px',
                borderColor: isDark
                  ? alpha('#f59e0b', 0.3)
                  : alpha('#f59e0b', 0.3),
                backgroundColor: isDark
                  ? alpha('#f59e0b', 0.1)
                  : alpha('#f59e0b', 0.1),
                color: isDark ? '#fbbf24' : '#d97706',
                fontSize: '0.85rem',
                fontWeight: 600,
                textTransform: 'none',
                px: 1.5,
                py: 0.8,
                minWidth: 'auto',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  borderColor: isDark ? '#fbbf24' : '#f59e0b',
                  backgroundColor: isDark
                    ? alpha('#fbbf24', 0.15)
                    : alpha('#f59e0b', 0.15),
                }
              }}
            >
              <AccountBalanceWallet sx={{ fontSize: 16, mr: 1, flexShrink: 0 }} />
              <Box component="span" sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                Balance
              </Box>
            </Button>
          ) : (
            <Chip
              label="No Data"
              size="small"
              sx={{
                backgroundColor: isDark
                  ? alpha('#64748b', 0.1)
                  : alpha('#64748b', 0.1),
                border: `1px solid ${alpha('#64748b', 0.2)}`,
                color: '#64748b',
                fontSize: '0.85rem',
                fontWeight: 500,
                width: '100%',
                justifyContent: 'center'
              }}
            />
          )}
        </Box>
      ),
      suppressSizeToFit: true,
    },
    {
      headerName: 'Actions',
      minWidth: 200,
      flex: 1.5,
      filter: false,
      sortable: false,
      // pinned: 'right',
      cellRenderer: (params) => (
        <Box sx={{
          display: 'flex',
          gap: 1,
          height: '100%',
          alignItems: 'center',
          py: 1
        }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Box component="span" sx={{ fontSize: '0.9rem' }}>✏️</Box>}
            onClick={() => handleEditSupplier(params.data)}
            sx={{
              borderRadius: '8px',
              borderColor: isDark
                ? alpha('#3b82f6', 0.3)
                : alpha('#10b981', 0.3),
              backgroundColor: isDark
                ? alpha('#3b82f6', 0.1)
                : alpha('#10b981', 0.1),
              color: isDark ? '#3b82f6' : '#10b981',
              fontSize: '0.85rem',
              fontWeight: 600,
              textTransform: 'none',
              px: 1,
              py: 0.5,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Box component="span" sx={{ fontSize: '0.9rem' }}>🗑️</Box>}
            onClick={() => handleDeleteClick(params.data.id)}
            sx={{
              borderRadius: '8px',
              borderColor: alpha('#ef4444', 0.3),
              backgroundColor: alpha('#ef4444', 0.1),
              color: '#ef4444',
              fontSize: '0.85rem',
              fontWeight: 600,
              textTransform: 'none',
              px: 1,
              py: 0.5,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            Delete
          </Button>
        </Box>
      ),
      suppressSizeToFit: true,
    },
  ], [isDark, handleEditSupplier, handleDeleteClick]);

  // Default column properties
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100,
    cellStyle: {
      borderRight: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(224, 224, 224, 0.5)',
      display: 'flex',
      alignItems: 'center',
    },
  }), [isDark]);

  // Grid options
  const gridOptions = useMemo(() => ({
    rowBuffer: 20,
    cacheBlockSize: 100,
    maxBlocksInCache: 10,
    animateRows: true,
    enableCellTextSelection: true,
    ensureDomOrder: true,
  }), []);

  // Update the filteredSuppliers calculation with proper type checking:
  const filteredSuppliers = useMemo(() => {
    if (!suppliers || !Array.isArray(suppliers)) return [];

    if (!searchTerm || searchTerm.trim() === '') {
      return suppliers;
    }

    const term = searchTerm.toLowerCase().trim();

    return suppliers.filter(supplier => {
      if (!supplier) return false;

      // Helper function to safely convert any value to searchable string
      const safeToString = (value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string') return value.toLowerCase();
        if (typeof value === 'number') return value.toString();
        if (typeof value === 'boolean') return value.toString();
        return String(value).toLowerCase();
      };

      // Safely get values for search
      const name = safeToString(supplier.name);
      const supplierShortcut = safeToString(supplier.supplierShortcut);
      const contact = safeToString(supplier.contact);
      const email = safeToString(supplier.email);
      const gstNumber = safeToString(supplier.gstNumber);
      const address = safeToString(supplier.address);
      const bankName = safeToString(supplier.bankName);
      const accountHolder = safeToString(supplier.accountHolder);
      const accountNumber = safeToString(supplier.accountNumber);
      const ifscCode = safeToString(supplier.ifscCode);
      const branch = safeToString(supplier.branch);
      const openingBalanceCash = safeToString(supplier.openingBalanceCash);
      const openingBalanceBank = safeToString(supplier.openingBalanceBank);
      const openingBalanceFine = safeToString(supplier.openingBalanceFine);
      const balanceValueCash = safeToString(supplier.balanceValueCash);
      const balanceValueBank = safeToString(supplier.balanceValueBank);
      const balanceFine = safeToString(supplier.balanceFine);

      // Check each field for the search term
      return (
        name.includes(term) ||
        supplierShortcut.includes(term) ||
        contact.includes(term) ||
        email.includes(term) ||
        gstNumber.includes(term) ||
        address.includes(term) ||
        bankName.includes(term) ||
        accountHolder.includes(term) ||
        accountNumber.includes(term) ||
        ifscCode.includes(term) ||
        branch.includes(term) ||
        openingBalanceCash.includes(term) ||
        openingBalanceBank.includes(term) ||
        openingBalanceFine.includes(term) ||
        balanceValueCash.includes(term) ||
        balanceValueBank.includes(term) ||
        balanceFine.includes(term)
      );
    });
  }, [suppliers, searchTerm]);
  // Add this useEffect to validate supplier data structure:
  useEffect(() => {
    if (suppliers && Array.isArray(suppliers)) {
      console.log('Suppliers data sample:', suppliers.slice(0, 3));
      suppliers.forEach((supplier, index) => {
        if (!supplier) {
          console.warn(`Supplier at index ${index} is null or undefined`);
        } else {
          // Log any unexpected data types
          Object.entries(supplier).forEach(([key, value]) => {
            if (value !== null && value !== undefined &&
              typeof value !== 'string' &&
              typeof value !== 'number' &&
              typeof value !== 'boolean') {
              console.warn(`Supplier ${index} (${supplier.name || 'unnamed'}): 
              Field "${key}" has unexpected type:`, typeof value, value);
            }
          });
        }
      });
    }
  }, [suppliers]);


  // Handlers
  const handleSaveSupplier = useCallback(async (values) => {
    setIsSaving(true);
    try {
      let result;
      const formData = { ...values };

      if (editingSupplier) {
        result = await updateSupplier(editingSupplier.id, formData);
      } else {
        formData.openingBalanceCash = formData.openingBalanceCash || 0;
        formData.openingBalanceBank = formData.openingBalanceBank || 0;
        formData.openingBalanceFine = formData.openingBalanceFine || 0;
        formData.balanceValueCash = formData.openingBalanceCash || 0;
        formData.balanceValueBank = formData.openingBalanceBank || 0;
        formData.balanceFine = formData.openingBalanceFine || 0;
        result = await addSupplier(formData);
      }

      setAlertMessage(result?.success ? 'Supplier saved successfully' : 'Failed to save supplier');
      setAlertType(result?.success ? 'success' : 'error');
      setDrawerOpen(false);
    } catch (error) {
      setAlertMessage('An error occurred');
      setAlertType('error');
    } finally {
      setIsSaving(false);
    }
  }, [editingSupplier, addSupplier, updateSupplier]);

  const handleDeleteSupplier = useCallback(async (id) => {
    try {
      const result = await deleteSupplier(id);
      setAlertMessage(result?.success ? 'Supplier deleted successfully' : 'Failed to delete supplier');
      setAlertType(result?.success ? 'success' : 'error');
    } catch (error) {
      setAlertMessage('An error occurred');
      setAlertType('error');
    }
    setDeleteConfirmOpen(false);
    setDeleteSupplierId(null);
  }, [deleteSupplier]);

  const handleCloseDrawer = useCallback(() => {
    if (!isSaving) {
      setDrawerOpen(false);
    }
  }, [isSaving]);

  const getInitialValues = useCallback(() => {
    if (editingSupplier) {
      return {
        name: editingSupplier.name || '',
        supplierShortcut: editingSupplier.supplierShortcut || '',
        contact: editingSupplier.contact || '',
        email: editingSupplier.email || '',
        address: editingSupplier.address || '',
        gstNumber: editingSupplier.gstNumber || '',
        openingBalanceCash: editingSupplier.openingBalanceCash || '',
        openingBalanceBank: editingSupplier.openingBalanceBank || '',
        openingBalanceFine: editingSupplier.openingBalanceFine || '',
        balanceValueCash: editingSupplier.balanceValueCash || '',
        balanceValueBank: editingSupplier.balanceValueBank || '',
        balanceFine: editingSupplier.balanceFine || '',
        bankName: editingSupplier.bankName || '',
        accountHolder: editingSupplier.accountHolder || '',
        accountNumber: editingSupplier.accountNumber || '',
        ifscCode: editingSupplier.ifscCode || '',
        branch: editingSupplier.branch || '',
      };
    }
    return initialValues;
  }, [editingSupplier]);

  useEffect(() => {
    if (alertMessage) {
      const timer = setTimeout(() => {
        setAlertMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alertMessage]);

  if (contextLoading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress
          size={40}
          sx={{
            color: isDark ? '#3b82f6' : '#10b981'
          }}
        />
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 600, color: isDark ? "#e0e0e0" : "#1a1a1a" }}>
          Loading Suppliers...
        </Typography>
      </Box>
    );
  }


  return (
    <Box sx={{
      minHeight: '100vh',
      py: 4,
      px: { xs: 2, sm: 3, md: 4 },
    }}>
      <Container maxWidth="xl" disableGutters>
        {/* Alert */}
        {alertMessage && (
          <Box sx={{ mb: 3, maxWidth: '100%' }}>
            <Alert
              severity={alertType}
              onClose={() => setAlertMessage('')}
              sx={{
                borderRadius: '16px',
                backgroundColor: isDark
                  ? alertType === 'success'
                    ? alpha('#10b981', 0.1)
                    : alpha('#ef4444', 0.1)
                  : alertType === 'success'
                    ? alpha('#10b981', 0.1)
                    : alpha('#ef4444', 0.1),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alertType === 'success'
                  ? alpha('#10b981', 0.2)
                  : alpha('#ef4444', 0.2)
                  }`,
                color: alertType === 'success' ? '#10b981' : '#ef4444',
              }}
            >
              {alertMessage}
            </Alert>
          </Box>
        )}

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: isDark
                ? 'rgba(30, 41, 59, 0.4)'
                : 'rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${isDark
                ? 'rgba(148, 163, 184, 0.1)'
                : 'rgba(255, 255, 255, 0.2)'
                }`,
              borderRadius: '24px',
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={3}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isDark
                    ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                    : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                }}>
                  <SupplierIcon sx={{ color: 'white', fontSize: 30 }} />
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      mb: 0.5,
                      background: isDark
                        ? 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)'
                        : 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      fontSize: { xs: '1.8rem', sm: '2.2rem' }
                    }}
                  >
                    Suppliers
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '1.1rem'
                    }}
                  >
                    Manage all your suppliers and vendor details
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  {/* Update the search TextField in the header: */}
                  <TextField
                    size="small"
                    placeholder="Search suppliers by name, contact, email, GST, address, bank..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    sx={{
                      minWidth: { xs: '100%', sm: 280 },
                      backgroundColor: isDark
                        ? 'rgba(30, 41, 59, 0.5)'
                        : 'rgba(255, 255, 255, 0.7)',
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        '&:hover': {
                          borderColor: isDark ? '#3b82f6' : '#10b981',
                        },
                        '&.Mui-focused': {
                          borderColor: isDark ? '#3b82f6' : '#10b981',
                          borderWidth: '2px',
                        }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{
                            color: isDark ? '#94a3b8' : '#718096'
                          }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchInput && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearchInput('')}
                            sx={{
                              padding: '4px',
                              color: isDark ? '#94a3b8' : '#718096',
                              '&:hover': {
                                color: isDark ? '#fff' : '#000',
                              }
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={refresh}
                      sx={{
                        borderRadius: '12px',
                        borderColor: isDark
                          ? 'rgba(148, 163, 184, 0.2)'
                          : 'rgba(226, 232, 240, 0.6)',
                        backgroundColor: isDark
                          ? 'rgba(30, 41, 59, 0.3)'
                          : 'rgba(255, 255, 255, 0.3)',
                        color: 'text.primary',
                        '&:hover': {
                          borderColor: isDark ? '#3b82f6' : '#10b981',
                          backgroundColor: isDark
                            ? alpha('#3b82f6', 0.1)
                            : alpha('#10b981', 0.1),
                        }
                      }}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddSupplier}
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        background: isDark
                          ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                          : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      }}
                    >
                      Add Supplier
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Stack>

            {/* Stats */}
            <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip
                label={`${filteredSuppliers.length} Total Suppliers`}
                sx={{
                  backgroundColor: isDark
                    ? alpha('#3b82f6', 0.1)
                    : alpha('#10b981', 0.1),
                  border: `1px solid ${isDark
                    ? alpha('#3b82f6', 0.2)
                    : alpha('#10b981', 0.2)
                    }`,
                  color: isDark ? '#3b82f6' : '#10b981',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  px: 1.5,
                  py: 1
                }}
              />
              {searchTerm && (
                <Chip
                  label={`${filteredSuppliers.length} results for "${searchTerm}"`}
                  onDelete={() => setSearchInput('')}
                  deleteIcon={<CloseIcon fontSize="small" />}
                  sx={{
                    backgroundColor: isDark
                      ? alpha('#8b5cf6', 0.1)
                      : alpha('#8b5cf6', 0.1),
                    border: `1px solid ${alpha('#8b5cf6', 0.2)}`,
                    color: '#8b5cf6',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    px: 1.5,
                    py: 1
                  }}
                />
              )}
            </Box>
          </Paper>
        </Box>

        {/* AG Grid Table */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: isDark
              ? 'rgba(30, 41, 59, 0.4)'
              : 'rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark
              ? 'rgba(148, 163, 184, 0.1)'
              : 'rgba(255, 255, 255, 0.2)'
              }`,
            borderRadius: '24px',
            overflow: 'hidden',
            height: 'calc(100vh - 300px)',
            minHeight: '500px',
          }}
        >
          <Box sx={{
            p: 3,
            borderBottom: `1px solid ${isDark
              ? 'rgba(148, 163, 184, 0.1)'
              : 'rgba(255, 255, 255, 0.2)'
              }`,
          }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.3rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              Supplier Records
              <Box component="span" sx={{
                fontSize: '0.9rem',
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                borderRadius: '12px',
                backgroundColor: isDark
                  ? alpha('#3b82f6', 0.15)
                  : alpha('#10b981', 0.15),
                border: `1px solid ${isDark
                  ? alpha('#3b82f6', 0.2)
                  : alpha('#10b981', 0.2)
                  }`,
                color: isDark ? '#60a5fa' : '#10b981'
              }}>
                {filteredSuppliers.length} Records
              </Box>
            </Typography>
          </Box>

          <Box
            className={isDark ? "ag-theme-alpine-dark" : "ag-theme-alpine"}
            sx={{
              width: '100%',
              height: 'calc(100% - 80px)',
              '& .ag-root-wrapper': {
                border: 'none',
                borderRadius: '0 0 24px 24px',
              },
              '& .ag-header': {
                background: isDark
                  ? 'linear-gradient(180deg, #252536 0%, #1e1e2d 100%)'
                  : 'linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%)',
                borderBottom: isDark
                  ? '1px solid rgba(255, 255, 255, 0.08)'
                  : '1px solid rgba(0, 0, 0, 0.06)',
              },
              '& .ag-header-cell': {
                fontWeight: '700',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                color: isDark ? '#94a3b8' : '#64748b',
              },
              '& .ag-row': {
                borderBottom: isDark
                  ? '1px solid rgba(255, 255, 255, 0.04)'
                  : '1px solid rgba(0, 0, 0, 0.03)',
                '&:hover': {
                  background: isDark
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(16, 185, 129, 0.05)',
                },
              },
            }}
          >
            <AgGridReact
              ref={gridRef}
              columnDefs={columnDefs}
              rowData={filteredSuppliers}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              rowHeight={68}
              headerHeight={52}
              animateRows={true}
              enableCellTextSelection={true}
              onGridReady={(params) => {
                params.api.sizeColumnsToFit();
                // setIsGridReady(true);
                window.addEventListener('resize', () => {
                  setTimeout(() => params.api.sizeColumnsToFit());
                });
              }}
            />
          </Box>
        </Paper>

        {/* Add/Edit Drawer with Formik */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleCloseDrawer}
          sx={{
            zIndex: theme => theme.zIndex.drawer + 2,
            '& .MuiDrawer-paper': {
              width: { xs: '100%', sm: 500 },
              backdropFilter: 'blur(20px)',
              background: isDark
                ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              borderLeft: `1px solid ${alpha(
                isDark ? theme.palette.divider : '#3b82f6',
                isDark ? 0.1 : 0.15
              )}`,
            }
          }}
        >
          <Formik
            initialValues={getInitialValues()}
            validationSchema={SupplierSchema}
            enableReinitialize
            onSubmit={handleSaveSupplier}
          >
            {(formik) => (
              <SupplierForm
                formik={formik}
                isDark={isDark}
                isSaving={isSaving}
                editingSupplier={editingSupplier}
                handleCloseDrawer={handleCloseDrawer}
              />
            )}
          </Formik>
        </Drawer>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          PaperProps={{
            sx: {
              backgroundColor: isDark
                ? 'rgba(30, 41, 59, 0.9)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(30px)',
              border: `1px solid ${isDark
                ? 'rgba(148, 163, 184, 0.2)'
                : 'rgba(255, 255, 255, 0.3)'
                }`,
              borderRadius: '24px',
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: 'text.primary' }}>
            Delete Supplier?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: 'text.secondary' }}>
              Are you sure you want to delete this supplier? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={() => setDeleteConfirmOpen(false)}
              sx={{
                borderRadius: '12px',
                borderColor: isDark
                  ? 'rgba(148, 163, 184, 0.2)'
                  : 'rgba(226, 232, 240, 0.6)',
                backgroundColor: isDark
                  ? 'rgba(30, 41, 59, 0.3)'
                  : 'rgba(255, 255, 255, 0.3)',
                color: 'text.primary',
                '&:hover': {
                  borderColor: isDark ? '#3b82f6' : '#10b981',
                  backgroundColor: isDark
                    ? alpha('#3b82f6', 0.1)
                    : alpha('#10b981', 0.1),
                }
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDeleteSupplier(deleteSupplierId)}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bank Details Dialog */}
        <BankDetailsModal
          open={bankDetailsOpen}
          onClose={() => setBankDetailsOpen(false)}
          supplier={selectedSupplier}
          isDark={isDark}
        />

        <LedgerBalanceModal
          open={ledgerBalanceOpen}
          onClose={() => setLedgerBalanceOpen(false)}
          supplier={selectedSupplier}
          isDark={isDark}
        />
      </Container>
    </Box>
  );
};

export default React.memo(SuppliersPage);